import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  BeadCrumb,
  TextField,
  Button,
  FormError,
  Typography,
  Checkbox,
  Pagination,
  NotificationModal,
  Select,
} from "components";
import roleSuccessImg from "assets/images/role-success-img.png";
import { isSuccessResponse, setAlert } from "utils/helper";
import makeRequest from "services/makeRequest";
import { BsSearch } from "react-icons/bs";
import { defaultValidation } from "utils/validators";
import { ROUTES } from "routes/routePaths";
import { routes } from "routes/apiRoutes";
import { Formik, Form, ErrorMessage } from "formik";
import { useNavigate, useLocation } from "react-router-dom";

import "./style.scss";
import { formatUrlQuery } from "utils/helper";

const CreateRole = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [state, setState] = useState({
    loading: false,
    permissions: null,
    searchParam: "",
  });

  const [filterState, setFilterState] = useState({
    number: 1,
    size: 20,
    assignable: "",
  });

  const { loading, permissions, searchParam } = state;

  const currentRole = location?.state?.currentRole;

  useEffect(() => {
    if (currentRole?.category?.toLowerCase() === "admin") {
      filterState.assignable = 2;
    } else {
      filterState.assignable = 1;
    }
    loadPermissions();
    //eslint-disable-next-line
  }, [filterState]);

  const loadPermissions = async () => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const { data } = await makeRequest(
        "get",
        routes.fetchPermissions(formatUrlQuery(filterState))
      );
      setState((prev) => ({
        ...prev,
        loading: false,
        permissions: data.data,
      }));
    } catch (err) {}
  };

  const handleSearch = ({ name, description }) => {
    let query = searchParam.toLowerCase().trim();
    return (
      name?.toLowerCase().includes(query) ||
      description?.toLowerCase().includes(query)
    );
  };

  const handlePermissionSelect = (permissions, permissionId) =>
    permissions.includes(Number(permissionId))
      ? permissions.filter((permission) => permission !== permissionId)
      : [...permissions, Number(permissionId)];

  const initialValues = {
    name: currentRole?.name || "",
    category: currentRole?.category?.toLowerCase() || "",
    description: currentRole?.description || "",
    permissions: currentRole?.permissions || [],
  };

  const validationSchema = Yup.object({
    name: defaultValidation("Role Name"),
    category: defaultValidation("Category"),
    description: defaultValidation("Description"),
  });

  const handleSubmit = async (values, resetForm) => {
    let method = currentRole ? "put" : "post";
    let url = currentRole
      ? routes.updateRole(currentRole?.id)
      : routes.createRole();

    values.category = values.category.toUpperCase();

    try {
      const { data } = await makeRequest(method, url, values);

      if (isSuccessResponse(data)) {
        if (currentRole) {
          setAlert("Role Updated Successfully", "success");
          navigate(ROUTES.manageRoles);
        } else {
          setShowModal(true);
          resetForm();
        }
      } else {
        setAlert(data.responseMessage, "error");
      }
      console.log(data);
    } catch (err) {}
  };

  return (
    <div className="dashboard-containter pt-4">
      <div className="beadcrumb-margin">
        <BeadCrumb titles={["Roles", "Role Management"]} />
      </div>
      <div className="role-create-container">
        <Typography variant="body0" component="h5" state="primary">
          {currentRole ? "Edit" : "Create a New"} Role
        </Typography>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
        >
          {({
            errors,
            touched,
            getFieldProps,
            setFieldValue,
            values,
            isSubmitting,
          }) => (
            <Form noValidate autoComplete="off">
              <div className="my-3">
                <Select
                  label="Select category"
                  placeholder="Select category"
                  options={[
                    { label: "Agent", value: "agent" },
                    { label: "Aggregator", value: "aggregator" },
                    { label: "Admin", value: "admin" },
                  ]}
                  name="category"
                  value={values.category}
                  onChange={(e) => {
                    const val = e.target.value;

                    setFieldValue("category", val);
                    if (val === "admin") {
                      filterState.assignable = 2;
                    } else {
                      filterState.assignable = 1;
                    }

                    filterState.number = 1;
                    loadPermissions();
                  }}
                  // {...getFieldProps("category")}
                  hasError={errors.category && touched.category}
                />
                <ErrorMessage component={FormError} name="category" />
              </div>

              <div className="my-3">
                <TextField
                  name="name"
                  type="text"
                  placeholder="Enter Role name"
                  label="Role Name"
                  {...getFieldProps("name")}
                  hasError={errors.name && touched.name}
                />
                <ErrorMessage component={FormError} name="name" />
              </div>

              <div className="my-3">
                <TextField
                  name="description"
                  type="text"
                  rows={4}
                  placeholder="Enter Role Description"
                  label="Role Description"
                  {...getFieldProps("description")}
                  hasError={errors.description && touched.description}
                />
                <ErrorMessage component={FormError} name="description" />
              </div>
              {values.category && (
                <>
                  <Typography variant="body2">
                    Add Permissions allowed for this role
                    <span
                      style={{ marginLeft: ".2rem" }}
                      className="font-danger"
                    >
                      *
                    </span>
                  </Typography>
                  <Typography variant="caption1">
                    All Permissions selected ({values.permissions.length})
                  </Typography>

                  <div className="permissions-container">
                    <div className="permission-search-container">
                      <TextField
                        name="searchParam"
                        type="text"
                        placeholder="Search by name or description"
                        onChange={(e) => {
                          setState((prev) => ({
                            ...prev,
                            searchParam: e.target.value,
                          }));
                        }}
                        required={false}
                        endIcon={<BsSearch />}
                        className="tiny-bordered-textfield"
                      />
                    </div>
                    {loading && (
                      <Typography
                        variant="body2"
                        className={"mt-2 text-center"}
                      >
                        Loading Permissions...
                      </Typography>
                    )}

                    <div className="mt-3 row">
                      {permissions?.content
                        ?.filter((permission) => handleSearch(permission))
                        .map(({ name, description, id }) => (
                          <div
                            className="col-md-6 mt-4 d-flex align-items-center"
                            key={id}
                          >
                            <Checkbox
                              name={name}
                              onChange={() =>
                                setFieldValue(
                                  "permissions",
                                  handlePermissionSelect(values.permissions, id)
                                )
                              }
                              checked={values.permissions.includes(id)}
                            />{" "}
                            &nbsp;
                            <div>
                              <Typography state="primary" variant="body4">
                                {name}
                              </Typography>
                              <Typography variant="caption1">
                                {description}
                              </Typography>
                            </div>
                          </div>
                        ))}
                    </div>
                    <div
                      style={{ width: "fit-content", margin: "4rem auto 0" }}
                    >
                      <Pagination
                        dataSize={permissions?.totalElements}
                        perPage={20}
                        handlePageClick={(pageNo) =>
                          setFilterState({ ...filterState, number: pageNo })
                        }
                        forcePage={filterState.number}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="d-flex justify-content-center">
                <Button
                  variant="secondary"
                  label="Cancel"
                  size="xs"
                  onClick={() => navigate(ROUTES.manageRoles)}
                />
                &nbsp; &nbsp;
                <Button
                  type="submit"
                  size="xs"
                  label={currentRole ? "Update" : "Create Role"}
                  disabled={!values.permissions.length}
                  loading={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <NotificationModal
        showModal={showModal}
        image={<img src={roleSuccessImg} alt="" width="120" />}
        onClose={() => {
          setShowModal(false);
        }}
        headerText="Role Created Successfully"
        bodyText="Role creation has been submitted for approval"
      />
    </div>
  );
};

export default CreateRole;
